import { Doc, generateToDocFn } from '@dadohr/shared/domainTypes/document';
import { ExperienceEvent } from '@dadohr/shared/domainTypes/event';
import {
  ExperienceEventType,
  IExperienceTemplate
} from '@dadohr/shared/domainTypes/experience';
import { IMailEvent } from '@dadohr/shared/domainTypes/mail';
import { FS } from '@dadohr/shared/versions';
import styled from '@emotion/styled';
import { Card, FormControl, MenuItem, Select } from '@material-ui/core';
import { groupBy, sortBy } from 'lodash';
import moment from 'moment-timezone';
import { useState } from 'react';
import { Loader } from '../../../../../../../components/Loader';
import { formatNumber } from '../../../../../../../components/Number';
import { TableCellLoader } from '../../../../../../../components/Table';
import { useErrorLogger } from '../../../../../../../hooks/useErrorLogger';
import { usePromise } from '../../../../../../../hooks/usePromise';
import { CanvasBar } from '../../../../../../../layout/Canvas';
import { FlexContainer } from '../../../../../../../layout/Flex';
import { Section, SectionTitle } from '../../../../../../../layout/Section';
import {
  store,
  useMappedLoadingValue
} from '../../../../../../../services/firebase';
import { Timestamp, fromMoment } from '../../../../../../../services/time';

const toMailEventDoc = generateToDocFn<IMailEvent>();
const toExperienceEventDoc = generateToDocFn<ExperienceEvent>();

const getEvents = async (tplId: string, cutoffDate: Timestamp) => {
  // TODO add index
  const allMailEvents = await store()
    .collection(FS.mailEventsV1)
    .where('tplId', '==', tplId)
    .get()
    .then((s) =>
      s.docs.map(toMailEventDoc).filter((d) => d.data.at > cutoffDate)
    );

  // TODO add index
  const expEvents = await store()
    .collection(FS.events)
    .where('expTId', '==', tplId)
    .get()
    .then((s) =>
      s.docs.map(toExperienceEventDoc).filter((d) => d.data.at > cutoffDate)
    );

  const mailEventsByInstanceId = groupBy(allMailEvents, (ev) => ev.data.expId);
  const expEventsByInstanceId = groupBy(expEvents, (ev) => ev.data.expId);

  return Object.entries(mailEventsByInstanceId).map(([expId, mailEvents]) => {
    const otherEvents = expEventsByInstanceId[expId] || [];
    const allEvents = sortBy([...mailEvents, ...otherEvents], (ev) =>
      ev.data.at.toMillis()
    );
    return {
      expId,
      events: allEvents
    };
  });
};

const summarize = (
  ds: {
    expId: string;
    events: (Doc<IMailEvent> | Doc<ExperienceEvent>)[];
  }[]
) => {
  const totalMailsDelivered = ds.filter((d) =>
    d.events.find((ev) => ev.data.type === 'Delivered')
  ).length;
  const uniqueOpens = ds.filter(
    (d) =>
      d.events.find(
        (ev) => ev.data.type === 'Opened' || ev.data.type === 'Clicked'
      ) // in case the opening eent was missed
  ).length;
  const clickedSomething = ds.filter((d) =>
    d.events.find((ev) => ev.data.type === 'Clicked')
  ).length;
  const completedSomething = ds.filter((d) =>
    d.events.find((ev) => ev.data.type === ExperienceEventType.SUB_TASK_DONE)
  ).length;
  const nowDone = ds.filter((d) =>
    d.events.find((ev) => ev.data.type === ExperienceEventType.EXP_DONE)
  ).length;

  const toPercent = (part: number) => part / totalMailsDelivered;

  return {
    mailsDelivered: {
      count: totalMailsDelivered,
      percent: toPercent(totalMailsDelivered)
    },
    uniqueOpens: {
      count: uniqueOpens,
      percent: toPercent(uniqueOpens)
    },
    clickedSomething: {
      count: clickedSomething,
      percent: toPercent(clickedSomething)
    },
    completedSomething: {
      count: completedSomething,
      percent: toPercent(completedSomething)
    }
  };
};

const StatsGrid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: '1fr max-content max-content',
  gridColumnGap: p.theme.spacing(3),
  gridRowGap: p.theme.spacing(1)
}));

const RAlign = styled('div')((p) => ({
  textAlign: 'right'
}));

export const Stats = ({ tpl }: { tpl: Doc<IExperienceTemplate> }) => {
  const [daysToLookout, setDaysToLookout] = useState(1);
  const tplId = tpl.id;
  const [stats, loading, error] = useMappedLoadingValue(
    usePromise(
      () => getEvents(tplId, fromMoment(moment().subtract(daysToLookout, 'd'))),
      [tplId, daysToLookout]
    ),
    summarize
  );
  useErrorLogger('event retrieval for stats', error);

  const toMailStatRow = (
    label: string,
    stat: { count: number; percent: number }
  ) => {
    return (
      <>
        <div>{label}</div>
        <RAlign>{stat.count}</RAlign>
        <RAlign>
          {formatNumber({
            n: stat.percent,
            format: 'percent',
            digits: 1
          })}
        </RAlign>
      </>
    );
  };

  return (
    <Section>
      <CanvasBar>
        <div>Stats</div>
        <div>
          <FormControl variant="outlined" size="small">
            <Select
              value={daysToLookout}
              onChange={(ev) => setDaysToLookout(Number(ev.target.value))}
            >
              <MenuItem value={1}>Last 24 hours</MenuItem>
              <MenuItem value={7}>Last 7 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
            </Select>
          </FormControl>
        </div>
      </CanvasBar>

      <Card>
        <SectionTitle>
          <FlexContainer justifyContent="space-between">
            <strong>Mails</strong>
            {loading && stats && <TableCellLoader />}
          </FlexContainer>
        </SectionTitle>

        {loading && !stats && <Loader height={126} />}
        {stats && (
          <StatsGrid>
            {toMailStatRow('Total delivered', stats.mailsDelivered)}
            {toMailStatRow('Unique opens', stats.uniqueOpens)}
            {toMailStatRow('Clicked something', stats.clickedSomething)}
            {toMailStatRow('Completed something', stats.completedSomething)}
          </StatsGrid>
        )}
      </Card>
    </Section>
  );
};
